import { useState, useEffect } from "react";
import { authAxios } from "../../utils/auth";
import { Search, ArrowRight, LogOut, Route } from "lucide-react";
import OnboardingWizardPanel from "../admin/OnboardingWizardPanel";

export default function JourneysTab({ range }) {
  const [sessionId, setSessionId] = useState("");
  const [sessionData, setSessionData] = useState(null);
  const [flow, setFlow] = useState(null);
  const [exits, setExits] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      authAxios.get(`/analytics/command/journey/flow?range=${range}`),
      authAxios.get(`/analytics/command/journey/exits?range=${range}`),
    ]).then(([flowRes, exitsRes]) => {
      setFlow(flowRes.data);
      setExits(exitsRes.data);
    }).catch(console.error);
  }, [range]);

  const lookupSession = async () => {
    if (!sessionId.trim()) return;
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/analytics/command/journey/session?sid=${sessionId.trim()}`);
      setSessionData(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* ── Session Lookup ── */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="journey-session-lookup">
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Search className="w-4 h-4" /> Session Lookup
        </h2>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={sessionId}
            onChange={e => setSessionId(e.target.value)}
            onKeyDown={e => e.key === "Enter" && lookupSession()}
            placeholder="Enter session ID..."
            className="flex-1 bg-[#0a0a0a] border border-white/10 text-white/80 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#C5A55A]/50"
            data-testid="session-id-input"
          />
          <button
            onClick={lookupSession}
            disabled={loading}
            className="px-4 py-2 bg-[#C5A55A]/20 text-[#C5A55A] text-sm rounded-lg hover:bg-[#C5A55A]/30 transition-colors disabled:opacity-50"
            data-testid="session-lookup-btn"
          >
            {loading ? "..." : "Lookup"}
          </button>
        </div>

        {sessionData?.session && (
          <div className="border border-white/[0.04] rounded-lg p-4 mb-4" data-testid="session-detail">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
              <div><span className="text-white/30">Pages:</span> <span className="text-white/80">{sessionData.session.page_count || 0}</span></div>
              <div><span className="text-white/30">Active:</span> <span className="text-white/80">{sessionData.session.total_active_seconds || 0}s</span></div>
              <div><span className="text-white/30">Entry:</span> <span className="text-white/80 font-mono">{sessionData.session.entry_path || "-"}</span></div>
              <div><span className="text-white/30">Exit:</span> <span className="text-white/80 font-mono">{sessionData.session.exit_path || "-"}</span></div>
            </div>
            {/* Timeline */}
            <div className="space-y-0">
              {sessionData.events?.map((ev, i) => (
                <div key={i} className="flex items-start gap-3 relative" data-testid={`session-event-${i}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-2 h-2 rounded-full mt-1.5 ${
                      ev.type === "pageview" ? "bg-[#C5A55A]" :
                      ev.type === "conversion" ? "bg-emerald-400" :
                      ev.type === "click" ? "bg-blue-400" : "bg-white/20"
                    }`} />
                    {i < sessionData.events.length - 1 && <div className="w-px h-6 bg-white/[0.06]" />}
                  </div>
                  <div className="flex-1 pb-2">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-white/50">{ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString() : ""}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        ev.type === "pageview" ? "bg-[#C5A55A]/15 text-[#C5A55A]" :
                        ev.type === "conversion" ? "bg-emerald-500/15 text-emerald-400" :
                        "bg-white/5 text-white/40"
                      }`}>{ev.type}</span>
                      {ev.path && <span className="text-white/60 font-mono truncate">{ev.path}</span>}
                      {ev.event_name && <span className="text-white/40">{ev.event_name}</span>}
                    </div>
                  </div>
                </div>
              ))}
              {(!sessionData.events || sessionData.events.length === 0) && (
                <p className="text-white/20 text-sm">No events found for this session</p>
              )}
            </div>
          </div>
        )}
        {sessionData && !sessionData.session && (
          <p className="text-white/30 text-sm">No session found with that ID</p>
        )}
      </section>

      {/* ── Page Flow ── */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="journey-flow">
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Route className="w-4 h-4" /> Page Flow ({flow?.total_sessions || 0} multi-page sessions)
        </h2>
        {flow?.transitions?.length > 0 ? (
          <div className="space-y-1.5">
            {flow.transitions.map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-1 hover:bg-white/[0.02] rounded px-2" data-testid={`flow-row-${i}`}>
                <span className="text-white/30 font-mono w-5">{i + 1}</span>
                <span className="text-white/60 font-mono flex-1 truncate">{t.from}</span>
                <ArrowRight className="w-3 h-3 text-[#C5A55A]/50 flex-shrink-0" />
                <span className="text-white/60 font-mono flex-1 truncate">{t.to}</span>
                <span className="text-white/80 font-mono font-bold w-10 text-right">{t.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-white/20 text-sm text-center py-8">No flow data available</div>
        )}
      </section>

      {/* ── Exit Pages ── */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="journey-exits">
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
          <LogOut className="w-4 h-4" /> Exit Pages
        </h2>
        {exits?.exits?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-white/30 border-b border-white/[0.06]">
                  <th className="text-left py-2 px-2 font-medium">#</th>
                  <th className="text-left py-2 px-2 font-medium">Page</th>
                  <th className="text-right py-2 px-2 font-medium">Exits</th>
                  <th className="text-right py-2 px-2 font-medium">Exit Rate</th>
                </tr>
              </thead>
              <tbody>
                {exits.exits.map((e, i) => (
                  <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                    <td className="py-2 px-2 text-white/25 font-mono">{i + 1}</td>
                    <td className="py-2 px-2 text-white/70 font-mono">{e.page}</td>
                    <td className="py-2 px-2 text-right text-white/50 font-mono">{e.exits}</td>
                    <td className="py-2 px-2 text-right font-mono">
                      <span className={e.exit_rate > 60 ? "text-red-400" : e.exit_rate > 30 ? "text-amber-400" : "text-emerald-400"}>
                        {e.exit_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-white/20 text-sm text-center py-8">No exit data available</div>
        )}
      </section>

      {/* Onboarding Wizard funnel (Brief 2026-02-27 §5 / Q8). */}
      <OnboardingWizardPanel days={parseInt(range, 10) || 30} />
    </div>
  );
}
